import { action, autorun, computed, makeAutoObservable } from 'mobx';
import { BonusModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/BonusModel';
import { Amount } from 'src_common/common/amount/Amount';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    FilterMenuItemType,
    FilterStateType,
} from 'src/domains/casino/webview/bonus-engine/campaign-list/filters/filters.type';

export type AvailableFreeBetType = {
    id: string;
    uid: string | null;
    type: string;
    selectionId: string | null;
    label: string;
    expiry: string;
    currentAmount: Amount;
    rawAmount: number;
};

export interface SelectionFreeBetsType {
    id: string | null;
    freeBets: string[];
}

export class RewardsNavigationState implements FilterStateType {
    public readonly searchInput: FormInputState<string, string>;
    public hideExpired: boolean = false;
    public activeModel: BonusModel | null = null;

    public selectedCampaignsSet = new Set<string>();
    public selectedRewards: string[] = [];
    public selectedStatuses: string[] = [];
    public selectedSources: string[] = [];

    public constructor(private readonly getBonusModels: () => Array<BonusModel> | 'loading') {
        this.searchInput = FormInputState.new('');

        autorun(() => {
            if (
                this.searchInput.value.length > 0 ||
                this.selectedCampaignsSet.size > 0 ||
                this.selectedRewards.length > 0 ||
                this.selectedStatuses.length > 0 ||
                this.selectedSources.length > 0
            ) {
                this.setActiveModel(null);
            }
        });

        makeAutoObservable(this);
    }

    public get isNoFilterSelected(): boolean {
        return (
            this.selectedCampaignsSet.size === 0 &&
            this.selectedRewards.length === 0 &&
            this.selectedStatuses.length === 0 &&
            this.selectedSources.length === 0
        );
    }

    private filterByExpired = (hideExpired: boolean, bonusModels: Array<BonusModel>): Array<BonusModel> => {
        const output: Array<BonusModel> = [];

        for (const model of bonusModels) {
            if (hideExpired) {
                if (model.status !== 'expired') {
                    output.push(model);
                }
            } else {
                output.push(model);
            }
        }
        return output;
    };

    @computed public get bonusesForVew(): Array<BonusModel> | 'loading' {
        const data = this.getBonusModels();

        if (data === 'loading') {
            return 'loading';
        }

        let filteredData = this.filterByExpired(this.hideExpired, data);

        if (this.searchInput.value.length > 0) {
            filteredData = filteredData.filter((model) =>
                model.rewardName?.toLowerCase().includes(this.searchInput.value.toLowerCase())
            );
        }

        if (this.selectedCampaignsSet.size > 0) {
            filteredData = filteredData.filter((model) => {
                const campaign = model.campaignModelWhenReady;
                return campaign !== null && this.selectedCampaignsSet.has(campaign.campaignType);
            });
        }

        if (this.selectedRewards.length > 0) {
            filteredData = filteredData.filter((model) => {
                return this.selectedRewards.includes(model.bonusType);
            });
        }

        if (this.selectedStatuses.length > 0) {
            filteredData = filteredData.filter((model) => {
                return this.selectedStatuses.includes(model.status);
            });
        }

        if (this.selectedSources.length > 0) {
            filteredData = filteredData.filter((model) => {
                return this.selectedSources.includes(model.bonusSource);
            });
        }

        return filteredData;
    }

    public onToggleExpired = (): void => {
        this.hideExpired = !this.hideExpired;
        this.setActiveModel(null);
    };

    public setActiveModel = (value: BonusModel | null): void => {
        this.activeModel = value;
    };

    @action public toggleSelectedCampaign = (campaignKey: string): void => {
        if (this.selectedCampaignsSet.has(campaignKey)) {
            this.selectedCampaignsSet.delete(campaignKey);
        } else {
            this.selectedCampaignsSet.add(campaignKey);
        }
    };

    @action public toggleSelectedReward = (rewardKey: string): void => {
        if (this.selectedRewards.includes(rewardKey)) {
            this.selectedRewards = this.selectedRewards.filter((r) => r !== rewardKey);
        } else {
            this.selectedRewards.push(rewardKey);
        }
    };

    @action public toggleSelectedStatus = (statusKey: string): void => {
        if (this.selectedStatuses.includes(statusKey)) {
            this.selectedStatuses = this.selectedStatuses.filter((s) => s !== statusKey);
        } else {
            this.selectedStatuses.push(statusKey);
        }
    };

    @action public toggleSelectedSource = (sourceKey: string): void => {
        if (this.selectedSources.includes(sourceKey)) {
            this.selectedSources = this.selectedSources.filter((s) => s !== sourceKey);
        } else {
            this.selectedSources.push(sourceKey);
        }
    };

    @action public clearFilters = (): void => {
        this.selectedCampaignsSet.clear();
        this.selectedRewards = [];
        this.selectedStatuses = [];
        this.selectedSources = [];
    };

    @computed public get filterMenuItems(): FilterMenuItemType[] {
        return [
            {
                label: 'Campaign',
                items: [
                    { key: 'bet-and-get', label: 'Bet and Get' },
                    { key: 'settlement', label: 'Settlement' },
                    { key: 'promo-code', label: 'Promo Code' },
                ],
            },
            {
                label: 'Reward',
                items: [
                    { key: 'free-bet', label: 'Free Bet' },
                    { key: 'free-spin', label: 'Free Spin' },
                ],
            },
            {
                label: 'Status',
                items: [
                    { key: 'active', label: 'Active' },
                    { key: 'draft', label: 'Draft' },
                    { key: 'paused', label: 'Paused' },
                    { key: 'expired', label: 'Expired' },
                    { key: 'canceled', label: 'Canceled' },
                ],
            },
            {
                label: 'Source',
                items: [
                    { key: 'automated', label: 'Bonus Engine Campaign' },
                    { key: 'manual', label: 'Manual' },
                    { key: 'optimove', label: 'Optimove' },
                ],
            },
        ];
    }
}
